import React, { useEffect, useState } from "react";
import {
  Dialog,
  IconButton,
  Box,
  Typography,
  useMediaQuery,
  Chip,
  Stack,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { TrendingUp } from "lucide-react";
import { getTopYieldingStocks } from "../../../api/api";
import { useNavigate } from "react-router-dom";
import { getCurrencySymbol } from "../../../utils/currency";

/* ================= HELPERS ================= */
const sym = getCurrencySymbol();

const fmtL = (n, d = 2) => n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

const formatPrice = (val) => {
  if (val === null || val === undefined) return "—";
  return `${sym}${fmtL(parseFloat(val))}`;
};

const formatYield = (val) => {
  if (val === null || val === undefined) return "—";
  return `${fmtL(parseFloat(val) * 100)}%`;
};

const formatPayoutRatio = (val) => {
  if (val === null || val === undefined) return "—";
  return `${fmtL(parseFloat(val) * 100)}%`;
};

const formatChange = (val) => {
  if (val === null || val === undefined) return "—";
  const num = parseFloat(val);
  return `${num >= 0 ? "+" : ""}${fmtL(num)}`;
};

const formatChangePct = (val) => {
  if (val === null || val === undefined) return "—";
  const num = parseFloat(val);
  return `${num >= 0 ? "+" : ""}${fmtL(num)}%`;
};

const formatMarketCap = (val) => {
  if (!val) return "—";
  const num = typeof val === "number" ? val : parseFloat(val);
  if (isNaN(num)) return "—";
  if (num >= 1e12) return `${sym}${fmtL(num / 1e12)}T`;
  if (num >= 1e9) return `${sym}${fmtL(num / 1e9)}B`;
  if (num >= 1e6) return `${sym}${fmtL(num / 1e6, 1)}M`;
  return `${sym}${num.toLocaleString()}`;
};

const formatScore = (val) => {
  if (val === null || val === undefined) return "—";
  return fmtL(parseFloat(val), 1);
};

/** Maps an overall_score number → a key in theme.palette.buckets */
// const scoreToBucketKey = (score) => {
//   const num = parseFloat(score);
//   if (num >= 71.07) return "strongBuy";
//   if (num >= 63.25) return "buy";
//   if (num >= 57.15) return "hold";
//   if (num >= 50.38) return "sell";
//   return "strongSell";
// };

/** Maps a letter grade → a key in theme.palette.buckets (same scale as FA Score) */
const gradeToBucketKey = (grade) => {
  const map = {
    "A+": "strongBuy", "A": "strongBuy", "A-": "strongBuy",
    "B+": "buy", "B": "buy", "B-": "buy",
    "C+": "hold", "C": "hold", "C-": "hold",
    "D+": "sell", "D": "sell", "D-": "sell",
    "F": "strongSell",
  };
  return map[grade] ?? null;
};


//1
/** Maps score → palette.buckets key */
const scoreToBucketKey = (score) => {
  const num = parseFloat(score);
  if (num >= 71.07) return "strongBuy";
  if (num >= 63.25) return "buy";
  if (num >= 57.15) return "hold";
  if (num >= 50.38) return "sell";
  return "strongSell";
};

/** Maps grade → palette.grades key */
const gradeToKey = (grade) => {
  const map = {
    "A+": "aPlus", "A": "a", "A-": "aMinus",
    "B+": "bPlus", "B": "b", "B-": "bMinus",
    "C+": "cPlus", "C": "c", "C-": "cMinus",
    "D+": "dPlus", "D": "d", "D-": "dMinus",
    "E+": "ePlus", "E": "e", "E-": "eMinus",
    "F": "f",
  };
  return map[grade] ?? "unknown";
};

//2
// /** Maps score → palette.grades key (mirrors grade scale) */
// const scoreToGradeKey = (score) => {
//   const num = parseFloat(score);
//   if (num >= 71.07) return "aPlus";
//   if (num >= 68.00) return "a";
//   if (num >= 63.25) return "aMinus";
//   if (num >= 60.00) return "bPlus";
//   if (num >= 57.15) return "b";
//   if (num >= 54.00) return "bMinus";
//   if (num >= 50.38) return "cPlus";
//   if (num >= 47.00) return "c";
//   if (num >= 43.00) return "cMinus";
//   if (num >= 40.00) return "dPlus";
//   if (num >= 36.00) return "d";
//   if (num >= 30.00) return "dMinus";
//   return "f";
// };

// /** Maps grade → palette.grades key */
// const gradeToKey = (grade) => {
//   const map = {
//     "A+": "aPlus",  "A": "a",   "A-": "aMinus",
//     "B+": "bPlus",  "B": "b",   "B-": "bMinus",
//     "C+": "cPlus",  "C": "c",   "C-": "cMinus",
//     "D+": "dPlus",  "D": "d",   "D-": "dMinus",
//     "F":  "f",
//   };
//   return map[grade] ?? "unknown";
// };


/* ================= STYLES ================= */

const thStyle = {
  padding: "12px 16px",
  fontSize: "var(--font-size-sm)",
  fontWeight: "var(--font-weight-bold)",
  color: "var(--color-text-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  textAlign: "left",
  position: "sticky",
  top: 0,
  background: "var(--color-surface)",
  backdropFilter: "blur(8px)",
  borderBottom: "2px solid var(--color-primary)",
  opacity: 0.95,
  zIndex: 1,
  whiteSpace: "nowrap",
};

/* ================= COMPONENT ================= */

const TopYieldingStocksDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredRow, setHoveredRow] = useState(null);

  /* ================= API ================= */

  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      setLoading(true);
      const res = await getTopYieldingStocks();

      if (res?.success && Array.isArray(res.data?.response_data)) {
        setStocks(res.data.response_data);
      } else {
        setStocks([]);
      }

      setLoading(false);
    };

    fetchData();
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          height: isMobile ? "100vh" : "85vh",
          borderRadius: isMobile ? 0 : 3,
          overflow: "hidden",
          bgcolor: "var(--color-bg-primary)",
          backgroundImage: "none",
        },
      }}
      TransitionComponent={Fade}
      transitionDuration={300}
    >
      {/* ================= HEADER ================= */}
      <Box sx={{ position: "sticky", top: 0, zIndex: 10 }}>
        <Box
          sx={{
            bgcolor: "var(--color-surface)",
            backdropFilter: "blur(16px)",
            px: 3,
            py: 2.5,
            borderBottom: "1px solid var(--color-border-primary)",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="title" color="var(--color-text-primary)">
              Top Yielding Stocks
            </Typography>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: "var(--color-text-primary)",
                bgcolor: "var(--color-surface-hover)",
                "&:hover": {
                  bgcolor: "var(--color-surface-active)",
                  transform: "rotate(90deg)",
                },
                transition: "all 0.3s ease",
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        {/* ================= TOOLBAR ================= */}
        <Box
          px={3}
          py={2.5}
          sx={{
            borderBottom: "1px solid var(--color-border-primary)",
            backgroundColor: "var(--color-surface)",
          }}
        >
          <Stack direction="row" alignItems="center" gap={1.5} mb={1.5}>
            <TrendingUp size={16} color="var(--color-primary)" />
            <Typography
              variant="paragraph"
              color="var(--color-text-muted)"
              textTransform="uppercase"
              letterSpacing="0.05em"
            >
              Dividend Yield Rankings
            </Typography>
          </Stack>

          <Typography
            variant="tableHeader"
            sx={{ color: "var(--color-text-secondary)", lineHeight: 1.6 }}
          >
            Data reflects prices with a 15-minute delay or the most recent market close.
          </Typography>
        </Box>
      </Box>

      {/* ================= TABLE ================= */}
      <Box flex={1} overflow="auto" sx={{ bgcolor: "var(--color-bg-secondary)" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            <tr>
              <th style={thStyle}>#</th>
              <th style={thStyle}>Ticker</th>
              <th style={thStyle}>Company</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Div Yield</th>
              {/* <th style={{ ...thStyle, textAlign: "right" }}>Payout Ratio</th> */}
              <th style={{ ...thStyle, textAlign: "right" }}>Price</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Change</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Change %</th>
              <th style={{ ...thStyle, textAlign: "right" }}>FA Score</th>
              <th style={{ ...thStyle, textAlign: "center" }}>FA Grade</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Market Cap</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={11}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "400px",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        border: "3px solid var(--color-border-primary)",
                        borderTopColor: "var(--color-primary)",
                        animation: "spin 1s linear infinite",
                        "@keyframes spin": {
                          "0%": { transform: "rotate(0deg)" },
                          "100%": { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                    <Typography variant="tableHeader" color="var(--color-text-muted)">
                      Loading top yielding stocks...
                    </Typography>
                  </Box>
                </td>
              </tr>
            ) : stocks.length === 0 ? (
              <tr>
                <td colSpan={11}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "200px",
                    }}
                  >
                    <Typography variant="tableHeader" color="var(--color-text-muted)">
                      No top yielding stocks found.
                    </Typography>
                  </Box>
                </td>
              </tr>
            ) : (
              stocks.map((row, idx) => {
                const isNeg = parseFloat(row.change_pct) < 0;
                const changeColor = isNeg ? "var(--color-error)" : "var(--color-success)";

                return (
                  <Fade
                    in
                    key={row.ticker}
                    timeout={300}
                    style={{ transitionDelay: `${idx * 30}ms` }}
                  >
                    <tr
                      onMouseEnter={() => setHoveredRow(row.ticker)}
                      onMouseLeave={() => setHoveredRow(null)}
                      onClick={() => {
                        onClose();
                        navigate(`/stocks/${row.ticker}/details/summary`);
                      }}
                      style={{
                        background:
                          hoveredRow === row.ticker
                            ? "var(--color-surface-hover)"
                            : "var(--color-surface)",
                        borderBottom: "1px solid var(--color-border-primary)",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {/* # Rank */}
                      <td style={{ padding: "14px 16px" }}>
                        <Typography
                          variant="paragraph"
                          sx={{ fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-muted)" }}
                        >
                          {idx + 1}
                        </Typography>
                      </td>

                      {/* Ticker */}
                      <td style={{ padding: "14px 16px" }}>
                        <Typography
                          variant="paragraph"
                          sx={{
                            fontWeight: "var(--font-weight-semibold)",
                            color: hoveredRow === row.ticker
                              ? "var(--color-success)"
                              : "var(--color-primary)",
                          }}
                        >
                          {row.ticker}
                        </Typography>
                      </td>

                      {/* Company Name */}
                      <td style={{ padding: "14px 16px" }}>
                        <Typography
                          variant="paragraph"
                          sx={{
                            fontWeight: "var(--font-weight-semibold)",
                            color: "var(--color-text-primary)",
                            maxWidth: 220,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                          }}
                          title={row.company_name}
                        >
                          {row.company_name}
                        </Typography>
                      </td>

                      {/* Dividend Yield — no bg, muted text matching Market Cap */}
                      <td style={{ padding: "14px 16px", textAlign: "right" }}>
                        <Chip
                          label={formatYield(row.dividend_yield)}
                          size="small"
                          sx={(t) => ({
                            ...t.typography.chipText,
                            bgcolor: "transparent",
                            color: "var(--color-text-secondary)",
                            fontWeight: "var(--font-weight-bold)",
                            height: 24,
                            minWidth: 60,
                          })}
                        />
                      </td>

                      {/* Payout Ratio — no bg, muted text matching Market Cap */}
                      {/* <td style={{ padding: "14px 16px", textAlign: "right" }}>
                        <Chip
                          label={formatPayoutRatio(row.dividend_payout_ratio)}
                          size="small"
                          sx={(t) => ({
                            ...t.typography.chipText,
                            bgcolor: "transparent",
                            color: "var(--color-text-secondary)",
                            fontWeight: "var(--font-weight-bold)",
                            height: 24,
                            minWidth: 60,
                          })}
                        />
                      </td> */}

                      {/* Price */}
                      <td style={{ padding: "14px 16px", textAlign: "right" }}>
                        <Typography
                          variant="paragraph"
                          sx={{ fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-primary)" }}
                        >
                          {formatPrice(row.price)}
                        </Typography>
                      </td>

                      {/* Change */}
                      <td style={{ padding: "14px 16px", textAlign: "right" }}>
                        <Typography
                          variant="paragraph"
                          sx={{ fontWeight: "var(--font-weight-semibold)", color: changeColor }}
                        >
                          {formatChange(row.change)}
                        </Typography>
                      </td>

                      {/* Change % */}
                      <td style={{ padding: "14px 16px", textAlign: "right" }}>
                        <Typography
                          variant="paragraph"
                          sx={{ fontWeight: "var(--font-weight-semibold)", color: changeColor }}
                        >
                          {formatChangePct(row.change_pct)}
                        </Typography>
                      </td>

                      {/* FA Score — solid bucket color bg, white text */}
                      <td style={{ padding: "14px 16px", textAlign: "right" }}>
                        {/* <Chip
                          label={formatScore(row.score)}
                          size="small"
                          sx={(t) => {
                            const color = t.palette.buckets[scoreToBucketKey(row.score)];
                            return {
                              ...t.typography.chipText,
                              bgcolor: color,
                              color: t.palette.text.inverse,
                              fontWeight: "var(--font-weight-bold)",
                              height: 24,
                              minWidth: 50,
                            };
                          }}
                        /> */}
                        <Chip
                          label={formatScore(row.score)}
                          size="small"
                          sx={(t) => {
                            const color = t.palette.buckets[scoreToBucketKey(row.score)];
                            return {
                              ...t.typography.chipText,
                              bgcolor: color,
                              color: t.palette.text.inverse,
                              fontWeight: "var(--font-weight-bold)",
                              height: 24,
                              minWidth: 50,
                            };
                          }}
                        />
                        {/* <Chip
                          label={formatScore(row.score)}
                          size="small"
                          sx={(t) => ({
                            ...t.typography.chipText,
                            bgcolor: t.palette.grades[scoreToGradeKey(row.score)],
                            color: t.palette.text.inverse,
                            fontWeight: "var(--font-weight-bold)",
                            height: 24,
                            minWidth: 50,
                          })}
                        /> */}
                      </td>

                      {/* Grade — same bucket color as FA Score for visual consistency */}
                      <td style={{ padding: "14px 16px", textAlign: "center" }}>
                        {/* <Chip
                          label={row.grade || "—"}
                          size="small"
                          sx={(t) => {
                            const key = gradeToBucketKey(row.grade);
                            const color = key ? t.palette.buckets[key] : t.palette.grades.unknown;
                            return {
                              ...t.typography.chipText,
                              bgcolor: color,
                              color: t.palette.text.inverse,
                              fontWeight: "var(--font-weight-bold)",
                              height: 24,
                              minWidth: 40,
                            };
                          }}
                        /> */}
                        <Chip
                          label={row.grade || "—"}
                          size="small"
                          sx={(t) => ({
                            ...t.typography.chipText,
                            bgcolor: t.palette.grades[gradeToKey(row.grade)],
                            color: t.palette.text.inverse,
                            fontWeight: "var(--font-weight-bold)",
                            height: 24,
                            minWidth: 40,
                          })}
                        />
                        {/* <Chip
                          label={row.grade || "—"}
                          size="small"
                          sx={(t) => ({
                            ...t.typography.chipText,
                            bgcolor: t.palette.grades[gradeToKey(row.grade)],
                            color: t.palette.text.inverse,
                            fontWeight: "var(--font-weight-bold)",
                            height: 24,
                            minWidth: 40,
                          })}
                        /> */}
                      </td>

                      {/* Market Cap */}
                      <td style={{ padding: "14px 16px", textAlign: "right" }}>
                        <Typography
                          variant="paragraph"
                          sx={{ fontWeight: "var(--font-weight-medium)", color: "var(--color-text-secondary)" }}
                        >
                          {formatMarketCap(row.market_cap)}
                        </Typography>
                      </td>
                    </tr>
                  </Fade>
                );
              })
            )}
          </tbody>
        </table>
      </Box>

      {/* ================= FOOTER ================= */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          bgcolor: "var(--color-surface)",
          backdropFilter: "blur(16px)",
          borderTop: "1px solid var(--color-border-primary)",
          px: 3,
          py: 2,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="var(--color-text-muted)">
            {loading ? "Loading..." : `${stocks.length} top yielding stocks`}
          </Typography>
          <Typography variant="caption" color="var(--color-text-muted)">
            Ordered by dividend yield
          </Typography>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default TopYieldingStocksDialog;